import { ChangeEvent, FormEvent, useCallback, useEffect, useState } from 'react';
import { Button, Label, Spinner, TextInput } from 'flowbite-react';
import { useMutation } from 'react-query';
import { changeEmailOrPhone } from 'src/apis/user';
import { useDebounce } from 'webrix/hooks';
import { useParams } from 'react-router-dom';
import { useToast } from 'src/context/ToastProvider';
import { useGetUniqueEmail } from 'src/hooks/user.hooks';
import { RegisterUserInput } from 'types';
import { isRecruiterSide } from 'src/config/env.config';
import { get } from 'lodash';
import { useThemeContext } from 'src/context/ThemeProvider';

type InputError = {
	message: string;
	type: 'success' | 'failure';
};
interface Props {
	setOtpSubmitted?: (otpSubmitted: boolean) => void;
	setPhoneOrEmail?: (phoneOrEmail: boolean) => void;
	userInfo: RegisterUserInput;
	setUserInfo: (userInfo: RegisterUserInput) => void;
}
const ChangeEmailForm = ({ setOtpSubmitted, setPhoneOrEmail, userInfo, setUserInfo }: Props) => {
	const { userName } = useParams();
	const toast = useToast();
	const [emailOrPhone, setEmailOrPhone] = useState<string>('');
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [inputHandlerError, setInputHandlerError] = useState<InputError>();
	const [emailError, setEmailError] = useState<InputError>();

	const userId = localStorage.getItem('userId') || '';
	const uniqueEmail = useGetUniqueEmail(emailOrPhone, userId, data => {
		if (data.success) {
			setEmailError(undefined);
			setEmailError({
				type: 'failure',
				message: `This Email id is already registered.`,
			});
		} else {
			setEmailError(undefined);
		}
	});

	useEffect(() => {
		if (!emailOrPhone) {
			setInputHandlerError(undefined);
		} else {
			uniqueEmail.refetch();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [emailOrPhone]);

	const changeEmailFn = useMutation({
		mutationKey: 'changeEmailOrPhone',
		mutationFn: changeEmailOrPhone,
		onSuccess() {
			toast({
				type: 'success',
				title: `An OTP has sent to your registered Email.`,
			});
			if (isRecruiterSide) {
				setUserInfo && setUserInfo({ ...userInfo, email: emailOrPhone });
			} else {
				// setUserInfo && setUserInfo({ ...userInfo, phone: emailOrPhone });
			}

			setOtpSubmitted && setOtpSubmitted(true);
			setPhoneOrEmail && setPhoneOrEmail(false);
			setEmailError(undefined);
			setIsLoading(false);
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				type: 'error',
				title: errorMessage,
			});
			setEmailError(undefined);
			setIsLoading(false);
		},
	});

	const changeHandler = (event: ChangeEvent<HTMLInputElement | HTMLInputElement>) => {
		setEmailOrPhone(event.target.value);
	};

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		// get userId from localstorage
		const userId = localStorage.getItem('userId') || '';
		changeEmailFn.mutate({ emailOrPhone: emailOrPhone?.trim().toLowerCase(), userId, isRecruiter: isRecruiterSide });
	};

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedChangeHandler = useCallback(useDebounce(changeHandler, 1000), [userName]);
	const { themeMode } = useThemeContext();
	const textInputStyle =
		themeMode === 'dark'
			? { color: 'white', backgroundColor: '#4B5563', border: '1px solid #6B7280' }
			: { color: 'black', backgroundColor: 'white', border: '1px solid #D1D5DB' };
	return (
		<div className="sm:w-full sm:max-w-lg px-4 pb-[40px]">
			<form className={'flex flex-col gap-4 shrink-0'} onSubmit={handleSubmit}>
				{isRecruiterSide ? (
					<p className="max-w-[358px] text-[#1C1C28]">
						Please enter your company email, we'll send you a verification code.
					</p>
				) : (
					<p className="max-w-[358px]">
						Please enter your phone number, we'll send <br />
						you a verification code.
					</p>
				)}

				<div className="md:w-[358px]">
					<span className="text-[12px] text-red-500">{emailError?.message}</span>
					<div className="mb-2 block">
						{isRecruiterSide ? (
							<Label className={'dark:text-[#fff] text-[#111928] font-inter'} htmlFor="email" value="Work Email " />
						) : (
							<Label className={'dark:text-[#fff] text-[#111928] font-inter'} htmlFor="phone" value="Phone " />
						)}
						<span className="text-red-600">*</span>
					</div>
					{isRecruiterSide ? (
						<TextInput
							id="email"
							sizing='lg'
							type="email"
							pattern={`^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$`}
							placeholder={'name@company.com'}
							required={true}
							defaultValue={emailOrPhone}
							color={inputHandlerError ? inputHandlerError.type : 'gray'}
							helperText={inputHandlerError ? inputHandlerError.message : undefined}
							onChange={e => debouncedChangeHandler(e)}
							style={textInputStyle}
						/>
					) : (
						<TextInput
							id="email"
							sizing='lg'
							type="email"
							pattern={`^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$`}
							placeholder={'name@provider.com'}
							required={true}
							defaultValue={emailOrPhone}
							color={inputHandlerError ? inputHandlerError.type : 'gray'}
							helperText={inputHandlerError ? inputHandlerError.message : undefined}
							onChange={e => debouncedChangeHandler(e)}
							style={textInputStyle}
						/>
					)}
				</div>

				<Button
					color="purplePlaced"
					data-testid="auth-signup"
					disabled={isLoading || !emailOrPhone || uniqueEmail.data?.success}
					type="submit"
					className={'signUp_form_btn md:w-[358px]'}
				>
					{isLoading ? <Spinner /> : 'Send OTP'}
				</Button>
			</form>
		</div>
	);
};

export default ChangeEmailForm;
